import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, CheckCircle, ArrowRight, Droplets, Wrench, Filter, Sparkles, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const SwimmingPoolConstruction = () => {
  const navigate = useNavigate();

  // Suppress this page from search engine indexing to prevent duplicate listings
  useEffect(() => {
    const existingNoindex = document.querySelector('meta[name="robots"][data-page-specific]');
    if (!existingNoindex) {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, follow';
      meta.setAttribute('data-page-specific', 'true');
      document.head.appendChild(meta);
    }
    return () => {
      const meta = document.querySelector('meta[name="robots"][data-page-specific]');
      if (meta) meta.remove();
    };
  }, []);

  const faqs = [
    {
      q: 'How much does swimming pool construction cost in Bengaluru?',
      a: 'Pool construction costs in Bengaluru typically range from Rs 5 Lakhs for a basic residential pool to Rs 50 Lakhs and above for luxury infinity pools. The final cost depends on pool size, design complexity, filtration system, and finishing materials. Contact Aquarist Pools for a detailed, obligation-free quote tailored to your project.'
    },
    {
      q: 'How long does it take to build a swimming pool?',
      a: 'A standard residential swimming pool takes 6 to 12 weeks from design approval to completion. Infinity pools and commercial projects may take longer depending on site conditions and design specifications. Aquarist Pools ensures timely delivery with dedicated project management.'
    },
    {
      q: 'What is a pipeless filtration system and why should I choose it?',
      a: 'Pipeless filtration systems like the FX-181 and NFX-201 eliminate traditional plumbing, sand filters, and messy pump rooms. They use advanced 5/15-micron German membrane technology, are wall-mounted or deck-integrated, and reduce maintenance significantly. Aquarist Pools is an authorised dealer of these revolutionary systems.'
    },
    {
      q: 'Do you offer pool maintenance and AMC services?',
      a: 'Yes, Aquarist Pools offers comprehensive pool maintenance and Annual Maintenance Contracts (AMC) covering water treatment, equipment servicing, cleaning, and emergency repairs. Our AMC plans ensure your pool stays crystal clear year-round.'
    },
    {
      q: 'Can you renovate or upgrade my existing swimming pool?',
      a: 'Absolutely. We specialise in swimming pool renovation services including retiling, waterproofing, equipment upgrades, pipeless filtration retrofits, LED lighting, and complete structural repairs. Our FX-181 wall-hanging filter is specifically designed for easy retrofit into existing pools.'
    },
    {
      q: 'What types of pools do you build?',
      a: 'Aquarist Pools builds residential pools, commercial pools, infinity pools, lap pools, plunge pools, and rooftop pools. We work with concrete, tile, and liner pool construction methods to suit every budget and requirement.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-[#003366] via-[#002244] to-[#001a33]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_rgba(0,174,239,0.3)_0%,transparent_70%)]"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <p className="text-[#00AEEF] font-semibold text-sm tracking-widest uppercase mb-4">Bengaluru's Trusted Pool Experts</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Best Swimming Pool Construction Company in Bengaluru
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
              Looking for a reliable <strong className="text-white">swimming pool builder</strong> near you? Aquarist Pools delivers expert <strong className="text-white">pool design, construction, renovation</strong>, and cutting-edge <strong className="text-white">pipeless filtration systems</strong> across Bengaluru and Karnataka.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                onClick={() => navigate('/booking')}
                className="bg-[#00AEEF] hover:bg-[#0098d4] text-white px-10 py-7 text-lg font-semibold shadow-xl hover:scale-105 transition-all duration-300"
              >
                Get Free Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                onClick={() => window.location.href = 'tel:+917757999805'}
                className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 px-10 py-7 text-lg font-semibold transition-all duration-300"
              >
                <Phone className="mr-2 h-5 w-5" /> Call Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-6">Why Aquarist Pools Is Bengaluru's Preferred Swimming Pool Builder</h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              When it comes to <strong>swimming pool construction</strong> in Bengaluru, Aquarist Pools and Fitness stands apart with over <strong>15 years of industry experience</strong>, a portfolio of stunning residential and commercial projects, and a commitment to quality that is second to none. Whether you are planning a backyard oasis, a rooftop infinity pool, or a commercial facility for a resort, our team of expert <strong>swimming pool contractors</strong> delivers every project on time and within budget.
            </p>
            <p>
              As a full-service <strong>swimming pool design and construction</strong> company, we handle every stage of the process — from initial consultation and 3D design to excavation, structural work, plumbing, tiling, and final commissioning. We use premium-grade materials, advanced waterproofing techniques, and energy-efficient equipment to build pools that last for decades.
            </p>
            <p>
              What truly sets us apart is our expertise in <strong>swimming pool pipeless filtration</strong> technology. As authorised dealers of FX-181 and NFX-201 pipeless filters, we offer a modern alternative to traditional sand filters — eliminating messy pump rooms, reducing maintenance, and delivering crystal-clear water with 5/15-micron German membrane technology.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">Our Swimming Pool Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Complete pool solutions from design to maintenance — everything under one roof.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center mb-5 group-hover:bg-[#00AEEF]/20 transition-colors">
                  <Droplets className="w-7 h-7 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">Swimming Pool Design & Construction</h3>
                <p className="text-gray-600 mb-4">Custom-designed residential and commercial pools built with premium materials. From lap pools and plunge pools to luxury infinity pools — we bring your vision to life with expert craftsmanship.</p>
                <p className="text-sm text-gray-500">
                  <em>Alt image: Swimming pool construction in progress at a Bengaluru residential site</em>
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center mb-5 group-hover:bg-[#00AEEF]/20 transition-colors">
                  <Sparkles className="w-7 h-7 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">Swimming Pool Renovation Services</h3>
                <p className="text-gray-600 mb-4">Breathe new life into your old pool. Our renovation services include retiling, waterproofing, structural repairs, equipment upgrades, and modern filtration retrofits. We transform outdated pools into stunning aquatic spaces.</p>
                <p className="text-sm text-gray-500">
                  <em>Alt image: Before and after swimming pool renovation by Aquarist Pools Bengaluru</em>
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center mb-5 group-hover:bg-[#00AEEF]/20 transition-colors">
                  <Filter className="w-7 h-7 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">Pipeless Filtration Systems</h3>
                <p className="text-gray-600 mb-4">Revolutionary FX-181 wall-hanging and NFX-201 deck-integrated filters with 5/15-micron German membranes. Eliminate traditional sand filters and pump rooms. Flow rates up to 30,000 litres per hour for pools up to 1,10,000 litres.</p>
                <Button variant="link" onClick={() => navigate('/pipeless-filtration')} className="text-[#00AEEF] p-0 h-auto font-semibold">
                  Explore Pipeless Filters <ArrowRight className="ml-1 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-xl bg-[#00AEEF]/10 flex items-center justify-center mb-5 group-hover:bg-[#00AEEF]/20 transition-colors">
                  <Wrench className="w-7 h-7 text-[#00AEEF]" />
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">Pool Maintenance & AMC</h3>
                <p className="text-gray-600 mb-4">Comprehensive Annual Maintenance Contracts covering water chemistry, equipment servicing, cleaning, and emergency repairs. Keep your pool in pristine condition year-round with our expert maintenance team.</p>
                <p className="text-sm text-gray-500">
                  <em>Alt image: Aquarist Pools maintenance team servicing a swimming pool in Bengaluru</em>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pool Construction Cost Section */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-6">Swimming Pool Construction Cost in Bengaluru</h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              One of the most common questions we receive is about <strong>pool construction cost</strong>. The investment depends on several factors including pool size, depth, design complexity (standard vs. <strong>infinity pool construction</strong>), choice of filtration system, and finishing materials.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mt-8">
            <Card className="border-2 border-gray-100 hover:border-[#00AEEF] transition-colors">
              <CardContent className="p-6 text-center">
                <p className="text-sm font-semibold text-gray-500 mb-2">BASIC RESIDENTIAL</p>
                <p className="text-3xl font-bold text-[#003366] mb-1">Rs 5-10L</p>
                <p className="text-sm text-gray-500">Standard design, basic filtration</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-[#00AEEF] shadow-lg">
              <CardContent className="p-6 text-center">
                <p className="text-sm font-semibold text-[#00AEEF] mb-2">PREMIUM RESIDENTIAL</p>
                <p className="text-3xl font-bold text-[#003366] mb-1">Rs 10-30L</p>
                <p className="text-sm text-gray-500">Custom design, pipeless filtration</p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-100 hover:border-[#00AEEF] transition-colors">
              <CardContent className="p-6 text-center">
                <p className="text-sm font-semibold text-gray-500 mb-2">LUXURY / COMMERCIAL</p>
                <p className="text-3xl font-bold text-[#003366] mb-1">Rs 30L+</p>
                <p className="text-sm text-gray-500">Infinity pools, resort-grade</p>
              </CardContent>
            </Card>
          </div>
          <p className="text-gray-600 mt-6 text-center">
            Every project is unique. <button onClick={() => navigate('/booking')} className="text-[#00AEEF] font-semibold underline hover:text-[#0098d4]">Contact Aquarist Pools</button> for an accurate, obligation-free estimate tailored to your requirements.
          </p>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-[#003366] text-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Aquarist Pools?</h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">The best swimming pool construction company near you — trusted by homeowners and businesses across Bengaluru.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              '15+ Years of Industry Experience',
              'End-to-End Pool Solutions',
              'Authorised Pipeless Filter Dealer',
              'Premium Quality Materials',
              'On-Time Project Delivery',
              'Comprehensive AMC Support',
              'Expert Design Team with 3D Visuals',
              'Competitive & Transparent Pricing',
              'Residential & Commercial Projects'
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
                <CheckCircle className="w-5 h-5 text-[#00AEEF] flex-shrink-0 mt-0.5" />
                <span className="text-gray-200">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section with Schema */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Everything you need to know about swimming pool construction in Bengaluru.</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <Card key={i} className="border-0 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-[#003366] mb-3">{faq.q}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#00AEEF] to-[#0098d4] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Build Your Dream Swimming Pool?</h2>
          <p className="text-xl mb-8 text-white/90">
            Get a free consultation and personalised quote from Bengaluru's most trusted swimming pool construction company.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/booking')}
              className="bg-white text-[#00AEEF] hover:bg-gray-100 px-10 py-7 text-lg font-bold shadow-xl hover:scale-105 transition-all duration-300"
            >
              Get Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              onClick={() => window.location.href = 'tel:+917757999805'}
              className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/40 px-10 py-7 text-lg font-semibold transition-all duration-300"
            >
              <Phone className="mr-2 h-5 w-5" /> 077579 99805
            </Button>
            <Button
              size="lg"
              onClick={() => window.open('https://www.google.com/maps/search/?api=1https://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery=Aquarist+Pools+and+Fitnesshttps://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmMquery_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmM', '_blank')}
              className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/40 px-10 py-7 text-lg font-semibold transition-all duration-300"
            >
              <MapPin className="mr-2 h-5 w-5" /> Visit Us
            </Button>
          </div>
          <p className="mt-6 text-white/70 text-sm">
            Serving Bengaluru, Electronic City, Whitefield, Sarjapur, HSR Layout, Koramangala, and all of Karnataka.
          </p>
        </div>
      </section>

      {/* FAQ Schema JSON-LD (injected via useEffect would be ideal, but static for SEO crawlers) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(f => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a }
            }))
          })
        }}
      />
    </div>
  );
};

export default SwimmingPoolConstruction;
